'use client';

import { useSession } from 'next-auth/react';
import { useRouter } from 'next/navigation';
import { useEffect, useState } from 'react';
import { Clock, Camera, CheckCircle2, AlertCircle, Image, X } from 'lucide-react';

interface HistoryEntry {
  id: string;
  customerName: string;
  date: string;
  summary: string;
  photoCount: number;
  photoUrls?: string[];
  status: 'sent' | 'pending' | 'failed';
}

interface GroupedHistory {
  date: string;
  entries: HistoryEntry[];
}

function groupByDate(entries: HistoryEntry[]): GroupedHistory[] {
  const groups: Record<string, HistoryEntry[]> = {};
  for (const entry of entries) {
    const d = new Date(entry.date);
    const key = d.toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'short',
      day: 'numeric',
    });
    if (!groups[key]) groups[key] = [];
    groups[key].push(entry);
  }
  return Object.entries(groups).map(([date, entries]) => ({ date, entries }));
}

function StatusBadge({ status }: { status: string }) {
  if (status === 'sent') {
    return (
      <span className="flex items-center gap-1 text-xs font-medium text-success">
        <CheckCircle2 className="w-3.5 h-3.5" /> Sent
      </span>
    );
  }
  if (status === 'failed') {
    return (
      <span className="flex items-center gap-1 text-xs font-medium text-error">
        <AlertCircle className="w-3.5 h-3.5" /> Failed
      </span>
    );
  }
  return (
    <span className="flex items-center gap-1 text-xs font-medium text-warning">
      <Clock className="w-3.5 h-3.5" /> Pending
    </span>
  );
}

function PhotoGallery({ urls }: { urls: string[] }) {
  const [selectedIdx, setSelectedIdx] = useState<number | null>(null);

  if (!urls || urls.length === 0) return null;

  return (
    <>
      <div className="flex gap-2 mt-2 overflow-x-auto pb-1">
        {urls.map((url, i) => (
          <button
            key={i}
            onClick={() => setSelectedIdx(i)}
            className="flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 border-base-200 hover:border-primary transition-colors"
          >
            <img
              src={url}
              alt={`Photo ${i + 1}`}
              className="w-full h-full object-cover"
              loading="lazy"
            />
          </button>
        ))}
      </div>

      {/* Lightbox */}
      {selectedIdx !== null && (
        <div
          className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center p-4"
          onClick={() => setSelectedIdx(null)}
        >
          <button
            className="absolute top-4 right-4 p-2 bg-white/20 rounded-full text-white hover:bg-white/30"
            onClick={() => setSelectedIdx(null)}
          >
            <X className="w-6 h-6" />
          </button>
          <div className="flex items-center gap-2 max-w-full max-h-full">
            {selectedIdx > 0 && (
              <button
                className="p-2 bg-white/20 rounded-full text-white text-2xl hover:bg-white/30 flex-shrink-0"
                onClick={(e) => { e.stopPropagation(); setSelectedIdx(selectedIdx - 1); }}
              >
                ‹
              </button>
            )}
            <img
              src={urls[selectedIdx]}
              alt={`Photo ${selectedIdx + 1}`}
              className="max-w-full max-h-[80vh] object-contain rounded-lg"
              onClick={(e) => e.stopPropagation()}
            />
            {selectedIdx < urls.length - 1 && (
              <button
                className="p-2 bg-white/20 rounded-full text-white text-2xl hover:bg-white/30 flex-shrink-0"
                onClick={(e) => { e.stopPropagation(); setSelectedIdx(selectedIdx + 1); }}
              >
                ›
              </button>
            )}
          </div>
          <div className="absolute bottom-6 left-0 right-0 text-center text-white/60 text-sm">
            {selectedIdx + 1} / {urls.length}
          </div>
        </div>
      )}
    </>
  );
}

export default function HistoryPage() {
  const { status } = useSession();
  const router = useRouter();
  const [history, setHistory] = useState<HistoryEntry[]>([]);
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (status === 'unauthenticated') {
      router.push('/login');
    }
  }, [status, router]);

  useEffect(() => {
    if (status === 'authenticated') {
      (async () => {
        setLoading(true);
        try {
          const res = await fetch('/api/history', { cache: 'no-store' });
          const data = await res.json();
          if (data.error) {
            setError(data.error);
          } else {
            setHistory(data.history || []);
            setName(data.name || '');
          }
        } catch {
          setError('Failed to load history');
        } finally {
          setLoading(false);
        }
      })();
    }
  }, [status]);

  if (status === 'loading' || status === 'unauthenticated') {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <span className="loading loading-spinner loading-lg text-primary" />
      </div>
    );
  }

  const grouped = groupByDate(history);

  return (
    <div className="min-h-screen bg-base-200 pb-24">
      {/* Header */}
      <div className="bg-gradient-to-br from-secondary to-secondary/80 text-white px-5 pt-8 pb-6">
        <div className="max-w-lg mx-auto">
          <div className="flex items-center gap-3">
            <Clock className="w-6 h-6" />
            <h1 className="text-xl font-bold">Service History</h1>
          </div>
          {name && <p className="mt-1 text-white/80 text-sm">{name}</p>}
        </div>
      </div>

      {/* Content */}
      <div className="max-w-lg mx-auto px-4 -mt-3">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-16 gap-3">
            <span className="loading loading-spinner loading-lg text-secondary" />
            <p className="text-base-content/50 text-sm">Loading history...</p>
          </div>
        ) : error ? (
          <div className="text-center py-16">
            <p className="text-error text-sm">{error}</p>
          </div>
        ) : history.length === 0 ? (
          <div className="text-center py-16">
            <Image className="w-12 h-12 mx-auto text-base-content/20 mb-4" />
            <h2 className="text-lg font-semibold text-base-content/60 mb-2">No submissions yet</h2>
            <p className="text-sm text-base-content/40">
              Your completed services will appear here after submission.
            </p>
          </div>
        ) : (
          <div className="flex flex-col gap-5 pt-2">
            {grouped.map((group) => (
              <div key={group.date}>
                <h2 className="text-xs font-semibold text-base-content/40 uppercase tracking-wider mb-2 px-1">
                  {group.date}
                </h2>
                <div className="flex flex-col gap-2">
                  {group.entries.map((entry) => {
                    const time = new Date(entry.date).toLocaleTimeString('en-US', {
                      hour: 'numeric',
                      minute: '2-digit',
                    });
                    return (
                      <div
                        key={entry.id}
                        className="bg-base-100 rounded-xl p-4 shadow-sm border border-base-200"
                      >
                        <div className="flex items-start justify-between mb-1.5">
                          <h3 className="font-semibold text-base-content text-sm">
                            {entry.customerName}
                          </h3>
                          <StatusBadge status={entry.status} />
                        </div>
                        <p className="text-xs text-base-content/50 mb-2">{time}</p>
                        {entry.summary && (
                          <p className="text-sm text-base-content/70 line-clamp-2 mb-2">
                            {entry.summary}
                          </p>
                        )}
                        {entry.photoUrls && entry.photoUrls.length > 0 ? (
                          <PhotoGallery urls={entry.photoUrls} />
                        ) : entry.photoCount > 0 ? (
                          <div className="flex items-center gap-1 text-xs text-base-content/40">
                            <Camera className="w-3.5 h-3.5" />
                            <span>{entry.photoCount} photo{entry.photoCount !== 1 ? 's' : ''}</span>
                          </div>
                        ) : null}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
